import { Component, computed, DestroyRef, inject, output, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InputLabel } from '../../../../parts/input-label/input-label.component';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { ChargeMarkAction, ManualPaymentType } from '../../../../core/enums/data.enums';
import { CustomValidators } from '../../../../core/validators/custom-validators';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { ChargesService } from '../../../../core/services/charge/charge.service';
import { MarkPropertyChargeRequest, PropertyChargeModel } from '../../../../core/models/data/property-charge.model';
import { BalancesMessagingConstants, GenericMessagingConstants } from '../../../../core/constants/messaging.constants';
import { DateUtilities } from '../../../../core/utilities/date.utilities';
import { BaseFormComponent } from '../../../../core/base-classes/base-form-component';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CanComponentDeactivate } from '../../../../core/routing/can-deactivate.guard';
import { DirtyFormRegistryService } from '../../../../core/services/dirty-form-registry.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { ChartOfAccountModel } from '../../../../core/models/data/chart-of-account.model';
import { DatePlaceholderDirective } from '../../../../core/directives/date-placeholder.directive';

@Component({
	selector: 'ms-mark-invoice-modal',
	imports: [
		CommonModule,
		ReactiveFormsModule,
		FontAwesomeModule,
		InputLabel,
		ConfirmationModalComponent,
		InfoModalComponent,
		EscCloseDirective,
		DatePlaceholderDirective
],
	templateUrl: './mark-invoice-modal.component.html'
})
export class MarkInvoiceModalComponent extends BaseFormComponent implements CanComponentDeactivate {

	//#region Properties & Signals

	public markCompleted = output<PropertyChargeModel>();

	public charge = signal<PropertyChargeModel | undefined>(undefined);
	public action = signal<ChargeMarkAction>(ChargeMarkAction.Paid);

	// The organization's system-assigned Stripe balance account. Shown read-only so the user
	// knows where the cash posts before confirming — it is never selectable here.
	public stripeBalanceAccount = signal<ChartOfAccountModel | undefined>(undefined);

	public voidConfirmationMessage = computed(() =>
		BalancesMessagingConstants.MARK_INVOICE_VOID_CONFIRMATION()
			.replaceValuesWithObject({ invoiceId: this.charge()?.propertyChargeId ?? '' })
	);

	public markForm: FormGroup;
	public ManualPaymentType = ManualPaymentType;
	public ChargeMarkAction = ChargeMarkAction;
	
	@ViewChild('markInvoiceConfirmationRef') confirmationRef!: ConfirmationModalComponent;
	@ViewChild('markInvoiceInfoRef') infoRef!: InfoModalComponent;
	@ViewChild('markInvoiceEscConfirmationRef') escConfirmationRef!: ConfirmationModalComponent;

	//#endregion Properties & Signals

	//#region Constructor

	private static readonly CHECK_NUMBER_VALIDATORS = [
		Validators.maxLength(30),
		Validators.pattern(/^[a-zA-Z0-9\-\/]*$/)
	];

	private fb = inject(FormBuilder);
	private chargesService = inject(ChargesService);
	private bootstrapService = inject(BootstrapService);
	private destroyRef = inject(DestroyRef);
	private dirtyFormRegistry = inject(DirtyFormRegistryService);
	private userSessionService = inject(UserSessionService);

	constructor() {
		super();

		this.destroyRef.onDestroy(() => this.dirtyFormRegistry.unregister(this));

		this.markForm = this.fb.group({
			paymentType: [ManualPaymentType.Check, [Validators.required]],
			checkNumber: ['', MarkInvoiceModalComponent.CHECK_NUMBER_VALIDATORS],
			paymentReceivedDate: [DateUtilities.todayISO(), [CustomValidators.noFutureDate('Payment date cannot be in the future')]],
			note: ['', [Validators.maxLength(500)]],
			sendEmail: [true]
		});

		this.setupPaymentTypeToggle();
	}

	//#endregion Constructor

	//#region Public Methods

	public open(charge: PropertyChargeModel, action: ChargeMarkAction): void {
		this.charge.set(charge);
		this.action.set(action);
		this.resetForm();
		this.dirtyFormRegistry.register(this);
		this.bootstrapService.manageModal('#markInvoiceModal', ModalActions.Show);

		if (action === ChargeMarkAction.Paid) {
			void this.loadStripeBalanceAccount();
		}
	}

	public get isPaidAction(): boolean {
		return this.action() === ChargeMarkAction.Paid;
	}

	public get isCheckPayment(): boolean {
		return this.markForm.get('paymentType')?.value === ManualPaymentType.Check;
	}

	//#endregion Public Methods

	//#region Form Submission

	public onSubmit(): void {
		if (this.isPaidAction && this.markForm.invalid) {
			this.markForm.markAllAsTouched();
			return;
		}

		const invoiceId = this.charge()?.propertyChargeId;
		const message = this.isPaidAction
			? BalancesMessagingConstants.MARK_INVOICE_PAID_CONFIRMATION().replace('{invoiceId}', String(invoiceId))
			: BalancesMessagingConstants.MARK_INVOICE_VOID_CONFIRMATION().replace('{invoiceId}', String(invoiceId));

		this.confirmationRef.modalType.set(ModalTypes.Question);
		this.confirmationRef.message.set(message);
		this.confirmationRef.confirmButtonText.set('Confirm');
		this.confirmationRef.cancelButtonText.set('Cancel');
		this.confirmationRef.confirmButtonClassName.set('btn btn-primary');

		this.bootstrapService.manageModal('#markInvoiceModal', ModalActions.Hide);
		this.bootstrapService.manageModal('#markInvoiceConfirmationModal', ModalActions.Show);
	}

	public async confirmSubmit(): Promise<void> {
		this.isSubmitting = true;
		this.confirmationRef.isSubmitting.set(true);

		try {
			const request = this.buildRequest();
			const result = await this.chargesService.markPropertyCharge(
				this.charge()!.propertyChargeId,
				request
			);

			this.bootstrapService.manageModal('#markInvoiceConfirmationModal', ModalActions.Hide);

			if (result.success && result.data) {
				this.submitSucceeded = true;
				this.infoRef.modalType.set(ModalTypes.Success);
				this.infoRef.message.set(
					this.isPaidAction
						? BalancesMessagingConstants.MARK_INVOICE_PAID_SUCCESS().replace('{invoiceId}', String(this.charge()!.propertyChargeId))
						: BalancesMessagingConstants.MARK_INVOICE_VOID_SUCCESS().replace('{invoiceId}', String(this.charge()!.propertyChargeId))
				);
				this.bootstrapService.manageModal('#markInvoiceInfoModal', ModalActions.Show);
				this.markCompleted.emit(result.data);
			} else {
				this.infoRef.modalType.set(ModalTypes.Failure);
				this.infoRef.message.set(result.message || BalancesMessagingConstants.MARK_INVOICE_FAILURE());
				this.bootstrapService.manageModal('#markInvoiceInfoModal', ModalActions.Show);
			}
		} catch (error) {
			this.bootstrapService.manageModal('#markInvoiceConfirmationModal', ModalActions.Hide);
			this.infoRef.modalType.set(ModalTypes.Failure);
			this.infoRef.message.set(BalancesMessagingConstants.MARK_INVOICE_FAILURE());
			this.bootstrapService.manageModal('#markInvoiceInfoModal', ModalActions.Show);
		} finally {
			this.isSubmitting = false;
			this.confirmationRef.isSubmitting.set(false);
		}
	}

	//#endregion Form Submission

	//#region Modal Management

	public onCancel(): void {
		if (this.markForm.dirty) {
			this.escConfirmationRef.modalType.set(ModalTypes.Question);
			this.escConfirmationRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
			this.bootstrapService.manageModal('#markInvoiceModal', ModalActions.Hide);
			this.bootstrapService.manageModal('#markInvoiceEscConfirmationModal', ModalActions.Show);
		} else {
			this.bootstrapService.manageModal('#markInvoiceModal', ModalActions.Hide);
			this.dirtyFormRegistry.unregister(this);
		}
	}

	public confirmationCancelled(): void {
		this.bootstrapService.manageModal('#markInvoiceConfirmationModal', ModalActions.Hide);
		this.bootstrapService.manageModal('#markInvoiceModal', ModalActions.Show);
	}

	public infoModalConfirmed(): void {
		this.dirtyFormRegistry.unregister(this);
	}

	public canDeactivate(): boolean | Promise<boolean> {
		return this.awaitLeaveConfirmation(this.markForm.dirty && !this.submitSucceeded, () => {
			this.escConfirmationRef.modalType.set(ModalTypes.Question);
			this.escConfirmationRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
			this.bootstrapService.manageModal('#markInvoiceModal', ModalActions.Hide);
			this.bootstrapService.manageModal('#markInvoiceEscConfirmationModal', ModalActions.Show);
		});
	}

	public onEscConfirmed(): void {
		this.bootstrapService.manageModal('#markInvoiceEscConfirmationModal', ModalActions.Hide);
		this.dirtyFormRegistry.unregister(this);
		this.resolvePendingLeave(true);
	}

	public onEscCancelled(): void {
		this.bootstrapService.manageModal('#markInvoiceEscConfirmationModal', ModalActions.Hide);
		this.bootstrapService.manageModal('#markInvoiceModal', ModalActions.Show);
		this.resolvePendingLeave(false);
	}

	//#endregion Modal Management

	//#region Private Helpers

	private async loadStripeBalanceAccount(): Promise<void> {
		this.stripeBalanceAccount.set(await this.userSessionService.getStripeBalanceAccount());
	}

	private setupPaymentTypeToggle(): void {
		const checkNumberControl = this.markForm.get('checkNumber') as AbstractControl;

		this.markForm.get('paymentType')!.valueChanges.pipe(
			takeUntilDestroyed(this.destroyRef)
		).subscribe((paymentType: ManualPaymentType) => {
			if (paymentType === ManualPaymentType.Check) {
				checkNumberControl.enable({ emitEvent: false });
				checkNumberControl.setValidators(MarkInvoiceModalComponent.CHECK_NUMBER_VALIDATORS);
			} else {
				checkNumberControl.clearValidators();
				checkNumberControl.setValue('', { emitEvent: false });
				checkNumberControl.disable({ emitEvent: false });
			}
			checkNumberControl.updateValueAndValidity({ emitEvent: false });
		});
	}

	private buildRequest(): MarkPropertyChargeRequest {
		const formValue = this.markForm.value;

		if (this.isPaidAction) {
			return {
				action: ChargeMarkAction.Paid,
				paymentType: formValue.paymentType,
				checkNumber: formValue.paymentType === ManualPaymentType.Check
					? (formValue.checkNumber?.trim() || undefined)
					: undefined,
				paymentReceivedDate: formValue.paymentReceivedDate || undefined,
				note: formValue.note?.trim() || undefined,
				sendEmail: formValue.sendEmail
			};
		} else {
			return {
				action: ChargeMarkAction.Void,
				note: formValue.note?.trim() || undefined,
				sendEmail: formValue.sendEmail
			};
		}
	}

	private resetForm(): void {
		this.submitSucceeded = false;
		const checkNumberControl = this.markForm.get('checkNumber') as AbstractControl;
		checkNumberControl.enable({ emitEvent: false });
		checkNumberControl.setValidators(MarkInvoiceModalComponent.CHECK_NUMBER_VALIDATORS);
		checkNumberControl.updateValueAndValidity({ emitEvent: false });

		this.markForm.reset({
			paymentType: ManualPaymentType.Check,
			checkNumber: '',
			paymentReceivedDate: DateUtilities.todayISO(),
			note: '',
			sendEmail: true
		});
	}

	//#endregion Private Helpers
}
